import { useCallback, useEffect, useRef, useState } from "react";
import { mergeFields } from "../../lib/autosave";

/**
 * 表单 / 弹窗的「边改边存」。
 *
 * 版本草稿已经实时保存（App 状态一变 1.5 秒后自动落库），表单只要把改动及时交给 App 就行。
 * 但不能每敲一个键就交：
 * - **不合法的不交**：空着、负数、没传完的图。不合法的项标红留在本地，App 里还是上一次合法的值
 * - **停手一会儿再交**（默认 500ms）：「12」会先经过「1」，有的 App 回调对中间值有副作用
 * - **关掉时把没交的交掉**：Esc / 点遮罩 / 切页，最后一笔不能丢
 * - **外面变了才同步进来**，而且只在本地没有未交改动时：App 每次重渲染都会传一个新对象，
 *   按引用比会把人正在填的东西冲掉（初始化配置页就踩过）。所以按 JSON 比
 *
 * toValue / fromValue：本地草稿和 App 里的形状不同时用（百分数 ×100 显示、多语言拆字段）。
 */
/*
 * 作用域：眼前在编的是哪个代理的哪一版（App 每次渲染写一次）。
 * 一笔改动记下它是在哪个作用域里改的；交的时候作用域变了（切了版本/代理）就扔掉，
 * 不能把 A 版的改动交进 B 版的状态里
 */
let currentScope = "";
export function setLiveDraftScope(scope: string) { currentScope = scope; }
/** 所有挂着的表单的 flush。切版本/代理之前先全交一遍，返回有没有交出东西 */
const registry = new Set<() => boolean>();
export function flushLiveDrafts(): boolean {
  let any = false;
  for (const f of registry) any = f() || any;
  return any;
}

export function useLiveDraft<T, D = T>(value: T, onCommit: (v: T) => void, opts: {
  valid?: (d: D) => boolean;
  /** false 时只改本地、不交（只读版本） */
  enabled?: boolean;
  delay?: number;
  toValue?: (d: D) => T;
  fromValue?: (v: T) => D;
} = {}) {
  const { delay = 500 } = opts;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const toV = (d: D) => (optsRef.current.toValue ? optsRef.current.toValue(d) : (d as unknown as T));
  const fromV = (v: T) => (optsRef.current.fromValue ? optsRef.current.fromValue(v) : (v as unknown as D));

  const [draft, setDraftState] = useState<D>(() => fromV(value));
  const draftRef = useRef(draft);
  /** 最近一次交出去 / 从外面同步进来的值（App 形状的 JSON） */
  const committedRef = useRef(JSON.stringify(value));
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** 最近一笔改动发生在哪个作用域 */
  const editScope = useRef(currentScope);

  const flush = useCallback((): boolean => {
    clearTimeout(timer.current);
    const o = optsRef.current;
    if (o.enabled === false) return false;
    const d = draftRef.current;
    if (o.valid && !o.valid(d)) return false;
    const v = toV(d);
    const j = JSON.stringify(v);
    if (j === committedRef.current) return false;
    if (editScope.current !== currentScope) return false; // 改动属于已经离开的版本/代理：不交
    committedRef.current = j;
    commitRef.current(v);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { registry.add(flush); return () => { registry.delete(flush); }; }, [flush]);

  const setDraft = useCallback((next: D | ((d: D) => D)) => {
    const v = typeof next === "function" ? (next as (d: D) => D)(draftRef.current) : next;
    editScope.current = currentScope;
    draftRef.current = v;
    setDraftState(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, delay);
  }, [flush, delay]);

  const valueJson = JSON.stringify(value);
  const scopeNow = currentScope;
  useEffect(() => {
    // 换了版本/代理：不管两边值一不一样，旧作用域里没交的（含标红的）都不带过来，整个换成新的
    if (editScope.current !== scopeNow) {
      clearTimeout(timer.current);
      editScope.current = scopeNow;
      committedRef.current = valueJson;
      const d = fromV(JSON.parse(valueJson) as T);
      draftRef.current = d;
      setDraftState(d);
      return;
    }
    if (valueJson === committedRef.current) return;
    const theirs = fromV(JSON.parse(valueJson) as T);
    const base = fromV(JSON.parse(committedRef.current) as T);
    const ours = draftRef.current;
    committedRef.current = valueJson;
    /*
     * 外面变了。本地没有未交的改动就整个跟过来；有的话按字段三方合并：
     * 本地没动过的字段取外面的，动过的留本地。
     * 不能「有改动就整个留本地」——自动保存刚把新建的萌宠标成已落库、给拼图发了 id，
     * 人这时在弹窗里改名字，整个留本地会把 persisted:false / puzzleId:null 又交回去，下一轮重复建
     */
    const merged = mergeFields(base, ours, theirs);
    draftRef.current = merged;
    setDraftState(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueJson, scopeNow]);

  useEffect(() => () => { flush(); }, [flush]);

  /** 放弃本地未交的改动，回到 App 里的值 */
  const reset = useCallback(() => {
    clearTimeout(timer.current);
    const d = fromV(JSON.parse(committedRef.current) as T);
    draftRef.current = d;
    setDraftState(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pending = JSON.stringify(toV(draft)) !== committedRef.current;
  return { draft, setDraft, flush, reset, pending };
}

